import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowDown, Check, Loader2, MessageSquare, Phone, Sparkles, Zap } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { CrumbBar } from "@/components/layout/Crumbs";
import api, { formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { GreenButton, GhostButton } from "@/components/Buttons";
import { Chapter, CursorTrail, GuideProvider, ScrollProgress, useGuide } from "@/components/webdemos/Guide";
import { useMall } from "@/context/MallContext";
import * as B from "@/components/webdemos/ButtonDemos";
import * as G from "@/components/webdemos/GalleryDemos";
import * as F from "@/components/webdemos/FormDemos";
import * as A from "@/components/webdemos/AppDemos";
import * as P from "@/components/webdemos/PassTwoDemos";
import { NYC } from "@/components/webdemos/assets";
import { PageBanner } from "@/components/mall/PageBanner";

const inputCls = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white placeholder:text-zinc-500 focus:border-brand-green focus:outline-none";

const DAYS = [
    ["Day 1", "Coffee", "We talk, take notes, sketch it on a park bench if that's where you are."],
    ["Day 2", "Design", "Rough layouts, color, type. You react — we adjust."],
    ["Day 3", "Design", "Everything locked. You approve the direction."],
    ["Day 4", "Build", "AI-assisted build. Real code, real pages, no template."],
    ["Day 5", "Build", "Backend wired, your content loaded, payments connected."],
    ["Day 6", "Polish", "Speed, mobile, edge cases. Every button feels right."],
    ["Day 7", "Launch", "Live URL. Training call. You keep the keys."],
];

const GETS = [
    "Custom, interactive site — not a template",
    "A backend you can actually edit",
    "Stripe payments live on day one",
    "Real design, tuned to your business",
    "AI capability that didn't exist last year",
    "Hosting, domain help + training call included",
];

function Hero() {
    return (
        <section className="dark-block relative overflow-hidden border-b border-white/10">
            <img src={NYC[0]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/85 to-brand-ink/50" />
            <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32">
                <Reveal>
                    <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-brand-blue">Website design · Brooklyn</p>
                    <h1 className="mt-5 font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl lg:text-7xl" data-testid="webdesign-title">
                        WE BUILD CUSTOM WEBSITES<br />FOR ANY BUSINESS.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base text-zinc-200 md:text-lg">
                        You do not have to be a print shop. We build fully custom websites for restaurants, contractors,
                        stores, studios and any other business. <span className="text-brand-green">We build them with AI and they go
                        live in 7 days.</span> An agency quotes $50,000 and six months for this. We build it in a week for a few
                        thousand dollars, and you get a real site with a real backend, not a template with your logo dropped in.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href="#playground"><GreenButton data-testid="hero-playground-cta"><Sparkles size={16} /> Play with 50+ features</GreenButton></a>
                        <a href="#quote"><GhostButton data-testid="hero-quote-cta"><Zap size={16} /> Get my site started</GhostButton></a>
                    </div>
                    <p className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                        <ArrowDown size={13} className="animate-bounce text-brand-green" /> Nothing below leaves this page. Touch everything.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

function ValueRow() {
    const { say } = useGuide();
    const rows = [
        ["$50k–$250k", "What an agency quotes for a custom, interactive site.", "Agencies bill hours. We bill outcomes — and AI does the boring 80%."],
        ["6 months", "How long that same site usually takes to ship.", "Six months is two seasons of sales you'll never get back."],
        ["7 days", "What it takes us — start to live URL.", "Day 1 coffee, day 7 live. That's the whole pitch."],
        ["A few thousand", "What you actually pay us.", "Same technology the big shops use, priced for a Brooklyn business."],
    ];
    return (
        <section className="border-b border-white/10 bg-white/[0.02]">
            <div className="mx-auto grid max-w-7xl gap-5 px-4 py-14 sm:px-6 md:grid-cols-4">
                {rows.map(([big, small, pitch], i) => (
                    <Reveal key={big} delay={i * 0.06}>
                        <button onClick={() => say(pitch, `value-${i}`)} className="w-full rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-left transition-colors hover:border-brand-green/40"
                            data-testid={`value-card-${i}`}>
                            <p className="font-display text-3xl font-black tracking-tight text-brand-green">{big}</p>
                            <p className="mt-2 text-sm text-zinc-300">{small}</p>
                            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">tap for the truth</p>
                        </button>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function Process() {
    const { say } = useGuide();
    return (
        <section id="process" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
            <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Park bench to live URL.</h2>
            <p className="mt-2 max-w-2xl text-base text-zinc-400">Seven days. You're involved for about two of them.</p>
            <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {DAYS.map(([day, title, desc], i) => (
                    <Reveal key={day} delay={i * 0.05}>
                        <button onClick={() => say(`${day} — ${desc}`, `day-${i}`)}
                            className="h-full w-full rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left transition-colors hover:border-brand-green/40"
                            data-testid={`process-day-${i}`}>
                            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-blue">{day}</p>
                            <p className="mt-2 font-display text-lg font-black">{title}</p>
                            <p className="mt-1.5 text-xs text-zinc-400">{desc}</p>
                        </button>
                    </Reveal>
                ))}
                <div className="rounded-2xl border border-brand-green/35 bg-brand-green/[0.07] p-5">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">Everything, day one</p>
                    <ul className="mt-3 space-y-1.5">
                        {GETS.map((g) => <li key={g} className="flex gap-2 text-xs text-zinc-200"><Check size={13} className="mt-0.5 shrink-0 text-brand-green" /> {g}</li>)}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Playground() {
    return (
        <>
            <section id="playground" className="border-y border-white/10 bg-white/[0.02]">
                <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-brand-blue">The playground</p>
                    <h2 className="mt-4 font-display text-3xl font-black tracking-tight sm:text-5xl">
                        TRY THE WHOLE INTERNET.<br /><span className="text-brand-green">THEN PICK YOUR FAVORITES.</span>
                    </h2>
                    <p className="mt-4 max-w-2xl text-base text-zinc-300">
                        Every card below is live. Click, drag, swipe, break it — the guide in the corner will tell you what
                        you're looking at and what it does for your business. Keep a mental list; we'll build the ones you liked.
                        <span className="text-brand-green"> Five chapters, 70+ things to touch.</span>
                    </p>
                </div>
            </section>

            <Chapter n={1} id="ch-buttons" accent="#2563EB" texture="dots"
                title="Buttons & micro-interactions"
                blurb="The smallest things on a site. Also the only things people actually touch."
                statement="Fifteen buttons. Pick the one that feels like your business and we'll build it.">
                <B.PlainButton /><B.GlowButton /><B.RippleButton />
                <B.LoadingButton /><B.MagneticButton /><B.IconButtons />
                <B.GradientButton /><B.CopyButton /><B.ConfettiButton />
                <B.LockedButton /><B.DestructiveButton /><B.VideoButton />
                <B.ArrowLinkDemo /><B.TiltCard /><B.SpotlightCard />
                <P.GlowCursorButtonDemo /><P.SoundToggleDemo /><P.ClickHeatDemo />
            </Chapter>

            <Chapter n={2} id="ch-motion" accent="#7dd3fc" texture="grid"
                title="Text, motion & attention"
                blurb="How a page holds someone for 90 seconds instead of four."
                statement="Movement is the difference between a brochure and a storefront that's open.">
                <B.CounterDemo /><B.MarqueeDemo /><B.TypewriterDemo />
                <G.FullscreenHeroDemo /><G.ParallaxDemo /><G.FullBleedDemo />
                <P.ScrollStoryDemo /><P.StickyCardsDemo />
                <P.CountdownDemo /><P.StatBarsDemo /><P.TickerNewsDemo />
                <P.LightModeDemo />
            </Chapter>

            <Chapter n={3} id="ch-galleries" accent="#fbbf24" texture="diagonal"
                title="Galleries & storytelling"
                blurb="Your work, shown a dozen different ways. Swipe, zoom, drag, deal, read."
                statement="If your work is good, the gallery is your whole sales team.">
                <G.CarouselDemo /><G.SwipeStripDemo />
                <G.LightboxDemo /><G.BeforeAfterDemo /><G.HoverZoomDemo />
                <G.MasonryDemo /><G.StackedCardsDemo />
                <G.ComicStripDemo /><G.VerticalFeedDemo />
                <P.ComicReaderDemo /><P.FlipCardDemo />
                <P.SplitHoverDemo /><P.HotspotDemo />
            </Chapter>

            <Chapter n={4} id="ch-forms" accent="#f472b6" texture="dots"
                title="Forms that actually get filled"
                blurb="Every extra field costs you a customer. None of these feel like forms."
                statement="A quote form nobody finishes is just a very expensive dead end.">
                <F.MultiStepForm /><F.LiveValidationForm /><F.SliderQuote />
                <F.StarRating /><F.FileDropDemo /><F.AutocompleteDemo />
                <F.ToggleDemo /><F.AccordionDemo /><F.DatePickerDemo />
                <F.StepperDemo /><P.TabsDemo /><P.TooltipDemo />
                <P.FilterChipsDemo /><P.DragDropDemo />
            </Chapter>

            <Chapter n={5} id="ch-money" accent="#fb923c" texture="grid"
                title="The parts that make money"
                blurb="Carts, quotes, bookings, dashboards. Real features, actually running right here."
                statement="This is the chapter that pays for the website in the first month.">
                <A.LiveInventoryDemo /><A.AddToCartDemo /><A.ConfiguratorDemo />
                <A.PricingTableDemo /><A.CouponDemo />
                <A.BookingDemo /><A.ChatQuoteDemo />
                <A.MapDemo /><A.DashboardDemo /><A.NotificationDemo />
                <A.OrderTrackerDemo /><A.ReviewsDemo /><A.PortalDemo />
                <P.LoyaltyCardDemo /><P.ModalDemo /><P.ToastStackDemo />
                <P.ScratchRevealDemo />
                <A.MallDemo />
            </Chapter>
        </>
    );
}

function Close() {
    const { tried } = useGuide();
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const { slug: activeSlug, homeSlug } = useMall();
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name || !form.email) { toast.error("Name and email required"); return; }
        setBusy(true);
        try {
            await api.post("/leads", { ...form, topic: "website-design", store_slug: activeSlug || homeSlug || null, message: `${form.message}${tried ? ` · played with ${tried} demo features` : ""}` });
            setDone(true);
            toast.success("Got it — we'll text you today.");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <section className="border-y border-white/10 bg-white/[0.02]">
                <div className="mx-auto max-w-5xl px-4 py-20 text-center sm:px-6">
                    {["You need a cool website like this.", "Or like anything you can think of.", "In only 7 days.", "It's a new World… Wide Web!"].map((line, i) => (
                        <Reveal key={line} delay={i * 0.08}>
                            <h2 className={`font-display text-3xl font-black leading-tight tracking-tight sm:text-5xl ${i % 2 ? "text-brand-green" : "text-white"}`}>{line}</h2>
                        </Reveal>
                    ))}
                    {tried > 0 && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400" data-testid="tried-summary">
                            you tried {tried} features on the way down · every one of them can be on your site
                        </motion.p>
                    )}
                </div>
            </section>

            <section id="quote" className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
                <div className="rounded-3xl border border-brand-green/30 bg-brand-green/[0.05] p-7 sm:p-10">
                    {done ? (
                        <div className="py-8 text-center" data-testid="web-done">
                            <Check size={44} className="mx-auto text-brand-green" />
                            <h2 className="mt-4 font-display text-3xl font-black">You're on the board.</h2>
                            <p className="mt-2 text-zinc-300">We'll text you today to book the coffee. Day 1 can be tomorrow.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} data-testid="web-quote-form">
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-green">Lock it in</p>
                            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">Tell us what you dreamed up.</h2>
                            <p className="mt-2 text-base text-zinc-300">
                                No deposit today — we talk first, quote flat, then start. Prefer typing?{" "}
                                <a href="sms:+13475733875?body=I%20want%20a%20new%20website." className="text-brand-green underline">Text (347) 573-3875</a>.
                            </p>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                <input className={inputCls} placeholder="Name" required value={form.name} onChange={set("name")} data-testid="web-name" />
                                <input className={inputCls} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="web-phone" />
                                <input className={`${inputCls} sm:col-span-2`} type="email" required placeholder="Email" value={form.email} onChange={set("email")} data-testid="web-email" />
                                <textarea className={`${inputCls} sm:col-span-2`} rows={4} placeholder="What's the business, and which demos above did you love?" value={form.message} onChange={set("message")} data-testid="web-message" />
                            </div>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <GreenButton type="submit" disabled={busy} data-testid="web-submit">
                                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Start my 7 days
                                </GreenButton>
                                <a href="tel:+13475733875"><GhostButton type="button"><Phone size={15} /> Call the shop</GhostButton></a>
                                <a href="sms:+13475733875?body=I%20want%20a%20new%20website."><GhostButton type="button"><MessageSquare size={15} /> Text instead</GhostButton></a>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </>
    );
}

export default function WebsiteDesign() {
    return (
        <Layout>
            <GuideProvider>
                <PageBanner room="website" />
                <CrumbBar trail={[{ label: "Website design" }]} />
                <ScrollProgress />
                <CursorTrail />
                <Hero />
                <ValueRow />
                <Process />
                <Playground />
                <Close />
            </GuideProvider>
        </Layout>
    );
}
